import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import StylishDateTimePicker from 'components/DesignSystems/form/StylishDateTimePicker';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import {
  useUpdateOperationalPeriod,
  useUpdateCurrentOperationalPeriod,
} from 'hooks/incident/useIncidentManagement';
import { usePeriods } from 'components/IAP/hooks/incidentHooks';
import dayjs from 'dayjs';
import CustomModal from 'components/DesignSystems/New/CustomModal';
import {
  useCurrentOperationalPeriod,
  useIncident,
} from 'components/IAP/hooks/incidentHooks';
import type { OperationalPeriod } from 'components/IAP/hooks/incidentHooks';
import { PeriodSection, ReadOnlyTimeTile } from './operationalPeriodModalUi';

interface OperationalPeriodModalProps {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  incident?: { id: string };
}

export type OperationalPeriodCurrentPickersRef = {
  getValues: () => { startIso: string; endIso: string | null };
};

type CurrentPickersProps = {
  opRow: OperationalPeriod;
  disabled: boolean;
};

const OperationalPeriodCurrentPickers = forwardRef<
  OperationalPeriodCurrentPickersRef,
  CurrentPickersProps
>(function OperationalPeriodCurrentPickers({ opRow, disabled }, ref) {
  const [currentStartIso, setCurrentStartIso] = useState(() =>
    dayjs(opRow.start_date_time).toISOString()
  );
  const [currentEndIso, setCurrentEndIso] = useState<string | null>(() =>
    opRow.anticipated_end_date_time
      ? dayjs(opRow.anticipated_end_date_time).toISOString()
      : null
  );

  useImperativeHandle(
    ref,
    () => ({
      getValues: () => ({
        startIso: currentStartIso,
        endIso: currentEndIso,
      }),
    }),
    [currentStartIso, currentEndIso]
  );

  return (
    <>
      <div className="bg-dark bg-opacity-25 mb-3 rounded p-3">
        <div className="text-uppercase txt--sml text-weak mb-3">Started</div>
        <div className="form-label">Date &amp; time</div>
        <StylishDateTimePicker
          value={
            currentStartIso && dayjs(currentStartIso).isValid()
              ? dayjs(currentStartIso).toDate()
              : dayjs(opRow.start_date_time).toDate()
          }
          onChange={(newValue) => {
            const v = newValue as Date | string | null;
            if (v instanceof Date) {
              setCurrentStartIso(v.toISOString());
            } else if (typeof v === 'string') {
              setCurrentStartIso(v);
            }
          }}
          type="datetime-local"
          disabled={disabled}
        />
      </div>
      <div className="bg-dark bg-opacity-25 rounded p-3">
        <div className="text-uppercase txt--sml text-weak mb-3">
          Anticipated end
        </div>
        <div className="form-label">Date &amp; time</div>
        <StylishDateTimePicker
          value={
            currentEndIso
              ? dayjs(currentEndIso).toDate()
              : opRow.anticipated_end_date_time
                ? dayjs(opRow.anticipated_end_date_time).toDate()
                : null
          }
          onChange={(newValue) => {
            const v = newValue as Date | string | null;
            if (v instanceof Date) {
              setCurrentEndIso(v.toISOString());
            } else if (typeof v === 'string') {
              setCurrentEndIso(v);
            }
          }}
          type="datetime-local"
          disabled={disabled}
        />
      </div>
    </>
  );
});

export const OperationalPeriodModal: React.FC<OperationalPeriodModalProps> = ({
  showModal,
  setShowModal,
  incident: propIncident,
}) => {
  const [date, setDate] = useState<Date | string | null>(null);
  const [previousExpanded, setPreviousExpanded] = useState(false);
  const currentPickersRef =
    React.useRef<OperationalPeriodCurrentPickersRef>(null);

  const { currentIncident: reduxIncident } = useSelector((state: unknown) => {
    return (state as { app: { currentIncident?: { id: string } } }).app;
  });
  const rbacPermissions = useSelector(
    (state: unknown) =>
      (
        state as {
          app: {
            validateRBACPermissionForActionResult?: Record<string, boolean>;
          };
        }
      ).app.validateRBACPermissionForActionResult
  );
  const canEditIncident = !!rbacPermissions?.['incident:create'];

  const incident = propIncident || reduxIncident;

  const previousMutation = useUpdateOperationalPeriod();
  const currentMutation = useUpdateCurrentOperationalPeriod();
  const periods = usePeriods();
  const { data: currentPeriod } = useCurrentOperationalPeriod();
  const { data: incidentDetails } = useIncident();
  const currentPeriodNumber = currentPeriod?.period_number ?? 0;
  const prevPeriod = periods.data?.find(
    (p) => p.period_number === currentPeriodNumber - 1
  );

  const opRow = currentPeriod ?? undefined;
  const currentStartDate = opRow?.start_date_time
    ? dayjs(opRow.start_date_time)
    : incidentDetails?.incident_created
      ? dayjs(incidentDetails.incident_created)
      : undefined;
  const currentAnticipatedEnd = opRow?.anticipated_end_date_time
    ? dayjs(opRow.anticipated_end_date_time)
    : undefined;

  const previousPeriodPickerValue: Date | null = (() => {
    if (date === null || date === undefined) {
      return prevPeriod?.end_date_time
        ? dayjs(prevPeriod.end_date_time).toDate()
        : null;
    }
    return date instanceof Date ? date : new Date(date);
  })();

  const willSavePrevious =
    currentPeriodNumber > 0 &&
    !!prevPeriod &&
    previousPeriodPickerValue !== null;

  const willSaveCurrent = !!opRow;

  const canPersistSomething = willSavePrevious || willSaveCurrent;

  const savePending = previousMutation.isPending || currentMutation.isPending;

  const showPreviousCard = currentPeriodNumber > 0 && !!prevPeriod;

  const periodSectionsCount = (showPreviousCard ? 1 : 0) + 1;
  const activeSectionsCount = opRow ? 1 : 0;

  const handleSaveAll = async () => {
    if (!incident?.id || !canEditIncident || !canPersistSomething) {
      return;
    }
    const incidentId = incident.id;
    try {
      if (willSavePrevious && previousPeriodPickerValue) {
        await previousMutation.mutateAsync({
          incidentId,
          newEndTime: previousPeriodPickerValue.toISOString(),
          operationalPeriod: currentPeriodNumber - 1,
          showToast: false,
        });
      }
      if (willSaveCurrent) {
        const snapshot = currentPickersRef.current?.getValues();
        if (!snapshot?.startIso) {
          return;
        }
        await currentMutation.mutateAsync({
          incidentId,
          startDateTime: snapshot.startIso,
          anticipatedEndDateTime: snapshot.endIso,
          showToast: false,
        });
      }
      toast.success('Operational period saved');
      setShowModal(false);
    } catch {
      // mutation hooks already surface errors
    }
  };

  const closeModal = () => {
    setDate(null);
    setPreviousExpanded(false);
    setShowModal(false);
  };

  const startedPrev = prevPeriod?.start_date_time
    ? dayjs(prevPeriod.start_date_time).format('MM/DD/YYYY [at] h:mm A')
    : '—';
  const endedPrev = prevPeriod?.end_date_time
    ? dayjs(prevPeriod.end_date_time).format('MM/DD/YYYY [at] h:mm A')
    : '—';

  return (
    <CustomModal
      show={showModal}
      heading="Operational periods"
      onClose={closeModal}
      onConfirm={() => {
        void handleSaveAll();
      }}
      saveButton="Save changes"
      buttonDisable={!canEditIncident || !canPersistSomething || savePending}
    >
      <div>
        {showPreviousCard && (
          <PeriodSection
            title={`Period ${prevPeriod.period_number}`}
            badge={<span className="badge bg-secondary">Completed</span>}
            headerAction={
              !previousExpanded && canEditIncident ? (
                <StylishNewButton
                  type="button"
                  className="button--secondary"
                  disabled={savePending}
                  onClick={() => setPreviousExpanded(true)}
                >
                  Edit
                </StylishNewButton>
              ) : null
            }
          >
            {previousExpanded ? (
              <>
                <div className="text-weak small mb-3">
                  Started (read-only): {startedPrev}
                </div>
                <div className="bg-dark bg-opacity-25 rounded p-3">
                  <div className="text-uppercase txt--sml text-weak mb-3">
                    Ended
                  </div>
                  <div className="form-label">Date &amp; time</div>
                  <StylishDateTimePicker
                    value={previousPeriodPickerValue}
                    onChange={(newDate) => {
                      setDate(newDate ? new Date(newDate) : null);
                    }}
                    type="datetime-local"
                    required={true}
                    disabled={!canEditIncident || savePending}
                  />
                </div>
              </>
            ) : (
              <div className="row g-2">
                <div className="col-md-6">
                  <ReadOnlyTimeTile
                    label="Started"
                    valueFormatted={startedPrev}
                  />
                </div>
                <div className="col-md-6">
                  <ReadOnlyTimeTile label="Ended" valueFormatted={endedPrev} />
                </div>
              </div>
            )}
          </PeriodSection>
        )}

        {opRow ? (
          <PeriodSection
            title={`Period ${currentPeriodNumber}${
              currentPeriodNumber === 0
                ? ' (ICS-201 development and briefing)'
                : ''
            }`}
            badge={<span className="badge bg-primary">Active</span>}
            highlight
          >
            {showModal ? (
              <OperationalPeriodCurrentPickers
                ref={currentPickersRef}
                key={`${opRow.id}-${opRow.start_date_time}-${opRow.anticipated_end_date_time ?? 'none'}`}
                opRow={opRow}
                disabled={!canEditIncident || savePending}
              />
            ) : null}
          </PeriodSection>
        ) : (
          <PeriodSection
            title="Active period"
            badge={<span className="badge bg-secondary">Unavailable</span>}
          >
            <div className="text-weak small">
              <div className="mb-2">
                Started:{' '}
                {currentStartDate?.isValid()
                  ? currentStartDate.format('MM/DD/YYYY HH:mm')
                  : '—'}
              </div>
              <div>
                Anticipated end:{' '}
                {currentAnticipatedEnd?.isValid()
                  ? currentAnticipatedEnd.format('MM/DD/YYYY HH:mm')
                  : '—'}
              </div>
            </div>
          </PeriodSection>
        )}

        <div className="text-weak txt--sml mt-2 mb-0">
          {periodSectionsCount} period section
          {periodSectionsCount === 1 ? '' : 's'} shown
          {activeSectionsCount > 0 ? ` · ${activeSectionsCount} active` : ''}
        </div>
      </div>
    </CustomModal>
  );
};
